import { useState, useEffect, useCallback } from 'react';
import { schoolApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'admin', label: 'Administrateur' },
  { value: 'direction', label: 'Direction' },
  { value: 'teacher', label: 'Enseignant' },
  { value: 'accounting', label: 'Comptabilite' },
];

const roleColors = {
  admin: 'bg-red-50 text-red-700',
  direction: 'bg-violet-50 text-violet-700',
  teacher: 'bg-blue-50 text-blue-700',
  accounting: 'bg-amber-50 text-amber-700',
};

export default function SchoolUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '', role: 'teacher', phone: '' });

  const loadData = useCallback(async () => {
    try { const res = await schoolApi.getUsers(); setUsers(res.data); }
    catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const set = (key) => (e) => setForm({ ...form, [key]: typeof e === 'string' ? e : e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await schoolApi.createUser(form);
      toast.success('Utilisateur cree');
      setDialogOpen(false);
      setForm({ email: '', password: '', first_name: '', last_name: '', role: 'teacher', phone: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Utilisateurs</h1>
          <p className="text-sm text-stone-500">{users.length} utilisateur(s)</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-user-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvel utilisateur</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prenom *</Label><Input value={form.first_name} onChange={set('first_name')} required data-testid="user-firstname" /></div>
                <div><Label>Nom *</Label><Input value={form.last_name} onChange={set('last_name')} required data-testid="user-lastname" /></div>
              </div>
              <div><Label>Email *</Label><Input type="email" value={form.email} onChange={set('email')} required data-testid="user-email" /></div>
              <div><Label>Mot de passe *</Label><Input type="password" value={form.password} onChange={set('password')} required minLength={6} data-testid="user-password" /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={set('role')}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="user-submit">Creer</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
      ) : users.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-stone-400">Aucun utilisateur</CardContent></Card>
      ) : (
        <Card className="border-stone-200">
          <div className="divide-y divide-stone-100">
            {users.map(u => (
              <div key={u.id} className="p-3 flex items-center justify-between hover:bg-stone-50" data-testid={`user-row-${u.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center">
                    <Shield className="w-4 h-4 text-stone-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-stone-900">{u.first_name} {u.last_name}</p>
                    <p className="text-xs text-stone-400">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`text-xs ${roleColors[u.role] || 'bg-stone-100 text-stone-600'}`}>
                    {ROLES.find(r => r.value === u.role)?.label || u.role}
                  </Badge>
                  <Badge variant="outline" className={`text-xs ${u.is_active ? 'border-green-300 text-green-600' : 'border-red-300 text-red-600'}`}>
                    {u.is_active ? 'Actif' : 'Inactif'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
